import { useState, useMemo } from "react";
import {
  Calculator,
  Users,
  Wallet,
  Briefcase,
  TrendingUp,
  ArrowRight,
  Info,
} from "lucide-react";

/**
 * The Bridge Model Sizing Calculator
 *
 * Lets a prospective host community enter population + median household income
 * and projects households served, budget, spoke staffing, and connector count
 * using the Bridge formula calibrated against the Baytown baseline.
 *
 * Baseline (Baytown):
 *   83,000 residents → 1,500 households served → $4.65M year-1 → 1:38 caseload
 *
 * Formula:
 *   households = round(population × 0.0181, -50)   // ~1.81% reach rate
 *   year1Budget = households × $3,100              // verified per-household annual
 *   connectors = ceil(households / 38)             // hard caseload cap
 *   perSpoke = households / 3                      // tri-portfolio default
 *   monthly = year1Budget / 12 / households        // ≈ $258
 *   fiveYearAllIn = year1Budget × 5.45             // includes 4% annual growth + closeout audit
 */

const COST_PER_HOUSEHOLD_ANNUAL = 3100;
const REACH_RATE = 0.0181;
const CASELOAD_CAP = 38;
const FIVE_YEAR_MULTIPLIER = 5.45;

// Reference points for the "where you fit" callout
const REFERENCE_CITIES = [
  { label: "North Houston District (Proposal 02)", pop: 67450, income: 41280 },
  { label: "Baytown (Proposal 01)", pop: 83000, income: 62450 },
  { label: "La Porte, TX (Tier 2)", pop: 35100, income: 79100 },
];

function needTier(income) {
  if (income <= 35000) return { label: "Acute Need", tone: "high", color: "#8B0000" };
  if (income <= 50000) return { label: "High Need", tone: "elevated", color: "#B8860B" };
  if (income <= 70000) return { label: "Moderate Need", tone: "moderate", color: "#1F2937" };
  return { label: "Lower Need · Discovery", tone: "discovery", color: "#475569" };
}

function fmtCurrency(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toLocaleString()}`;
}

function fmtNumber(n) {
  return n.toLocaleString();
}

export default function SizingCalculator() {
  const [population, setPopulation] = useState(67450);
  const [income, setIncome] = useState(41280);

  const projection = useMemo(() => {
    const pop = Math.max(0, Number(population) || 0);
    const households = Math.max(
      100,
      Math.round((pop * REACH_RATE) / 50) * 50
    );
    const year1Budget = households * COST_PER_HOUSEHOLD_ANNUAL;
    const connectors = Math.ceil(households / CASELOAD_CAP);
    const perSpoke = Math.round(households / 3);
    const monthly = households > 0 ? Math.round(year1Budget / 12 / households) : 0;
    const fiveYear = Math.round(year1Budget * FIVE_YEAR_MULTIPLIER);
    const tier = needTier(Number(income) || 0);
    return { households, year1Budget, connectors, perSpoke, monthly, fiveYear, tier };
  }, [population, income]);

  const apply = (ref) => {
    setPopulation(ref.pop);
    setIncome(ref.income);
  };

  return (
    <section
      id="sizing"
      data-testid="sizing-calculator"
      className="bg-gradient-to-b from-[#FDF8EF] to-[#F4EBD8] py-20 lg:py-28 border-y border-[#E8DCC3]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#B8860B]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#B8860B]">
                Self-Serve Qualification · For Prospective Host Communities
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2937] leading-tight">
              The Bridge<br />
              <em className="italic text-[#B8860B]">Sizing Calculator.</em>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-3 text-[#4B5563] leading-relaxed">
            <p>
              Enter your community&apos;s population and median household income.
              The calculator projects households served per year, Year-1 budget,
              connector staffing, and 5-year all-in cost using the Bridge formula —
              calibrated against the Baytown Proposal 01 baseline.
            </p>
            <p className="mt-3 text-xs font-mono uppercase tracking-wider text-[#6B7280]">
              Reach rate · 1.81% of population &nbsp;·&nbsp; $3,100/household/yr &nbsp;·&nbsp; 1:38 caseload cap
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-px bg-[#E8DCC3] border border-[#E8DCC3]">
          {/* INPUTS */}
          <div
            data-testid="sizing-inputs"
            className="lg:col-span-5 bg-white p-7 lg:p-9 flex flex-col gap-7"
          >
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 bg-[#1F2937] text-[#FDF8EF]">
                <Calculator className="h-5 w-5" />
              </span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#B8860B]">
                  Step 01
                </div>
                <h3 className="font-display text-xl text-[#1F2937]">Tell us about your community</h3>
              </div>
            </div>

            <div>
              <label
                htmlFor="sizing-pop"
                className="font-mono text-[10px] uppercase tracking-widest text-[#4B5563] block mb-2"
              >
                Resident Population
              </label>
              <input
                id="sizing-pop"
                data-testid="sizing-input-population"
                type="number"
                min="0"
                step="500"
                value={population}
                onChange={(e) => setPopulation(e.target.value)}
                className="w-full bg-[#FDF8EF] border border-[#E8DCC3] focus:border-[#B8860B] outline-none px-4 h-12 font-display text-xl text-[#1F2937] transition-colors"
              />
              <div className="flex justify-between mt-2 text-[10px] font-mono uppercase tracking-wider text-[#6B7280]">
                <span>Smaller districts</span>
                <span>Large entitlement cities</span>
              </div>
              <input
                type="range"
                min="5000"
                max="300000"
                step="500"
                value={Math.min(300000, Math.max(5000, Number(population) || 5000))}
                onChange={(e) => setPopulation(e.target.value)}
                className="w-full mt-1 accent-[#B8860B]"
                data-testid="sizing-slider-population"
              />
            </div>

            <div>
              <label
                htmlFor="sizing-inc"
                className="font-mono text-[10px] uppercase tracking-widest text-[#4B5563] block mb-2"
              >
                Median Household Income (USD)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280] font-display text-lg">$</span>
                <input
                  id="sizing-inc"
                  data-testid="sizing-input-income"
                  type="number"
                  min="0"
                  step="500"
                  value={income}
                  onChange={(e) => setIncome(e.target.value)}
                  className="w-full bg-[#FDF8EF] border border-[#E8DCC3] focus:border-[#B8860B] outline-none pl-8 pr-4 h-12 font-display text-xl text-[#1F2937] transition-colors"
                />
              </div>
              <input
                type="range"
                min="20000"
                max="120000"
                step="500"
                value={Math.min(120000, Math.max(20000, Number(income) || 20000))}
                onChange={(e) => setIncome(e.target.value)}
                className="w-full mt-2 accent-[#B8860B]"
                data-testid="sizing-slider-income"
              />
            </div>

            {/* Reference quick-picks */}
            <div className="border-t border-[#E8DCC3] pt-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#B8860B] mb-3">
                Compare to a known deployment
              </div>
              <div className="flex flex-wrap gap-2">
                {REFERENCE_CITIES.map((ref) => (
                  <button
                    key={ref.label}
                    type="button"
                    data-testid={`sizing-ref-${ref.label.split(" ")[0].toLowerCase()}`}
                    onClick={() => apply(ref)}
                    className="px-3 h-9 font-mono text-[10px] uppercase tracking-widest border border-[#1F2937] text-[#1F2937] hover:bg-[#1F2937] hover:text-[#FDF8EF] transition-colors"
                  >
                    {ref.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* OUTPUTS */}
          <div
            data-testid="sizing-outputs"
            className="lg:col-span-7 bg-[#1F2937] text-[#FDF8EF] p-7 lg:p-9 relative overflow-hidden"
          >
            <div
              aria-hidden
              className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-[#B8860B]/10 blur-3xl pointer-events-none"
            />

            <div className="relative z-10">
              <div className="flex items-start justify-between gap-4 mb-7">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#B8860B]">
                    Projected Bridge Proposal · Your Community
                  </div>
                  <h3 className="font-display text-2xl lg:text-3xl mt-1">
                    {fmtNumber(Number(population) || 0)} residents · MHI {fmtCurrency(Number(income) || 0)}
                  </h3>
                </div>
                <span
                  data-testid="sizing-need-tier"
                  className="font-mono text-[10px] uppercase tracking-widest px-3 h-7 inline-flex items-center"
                  style={{ backgroundColor: projection.tier.color, color: "#FDF8EF" }}
                >
                  {projection.tier.label}
                </span>
              </div>

              {/* Hero output: Households served */}
              <div className="border-y border-[#FDF8EF]/15 py-7 mb-7">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#B8860B] mb-2">
                  Households Served · Year 1
                </div>
                <div
                  data-testid="sizing-output-households"
                  className="font-display text-6xl lg:text-7xl text-[#FDF8EF] leading-none"
                >
                  {fmtNumber(projection.households)}
                </div>
                <p className="text-sm text-[#CBD5E1] mt-3 max-w-md">
                  Three competitively-selected Spoke agencies, each running a portfolio of{" "}
                  <strong className="text-[#FDF8EF]">{fmtNumber(projection.perSpoke)} households</strong>.
                </p>
              </div>

              {/* Stat grid */}
              <div className="grid grid-cols-2 gap-4 mb-7">
                <Stat
                  testId="sizing-output-budget"
                  icon={Wallet}
                  label="Year-1 Investment"
                  value={fmtCurrency(projection.year1Budget)}
                  sub={`≈ $${projection.monthly}/household/month`}
                />
                <Stat
                  testId="sizing-output-connectors"
                  icon={Users}
                  label="Service Connectors"
                  value={projection.connectors.toString()}
                  sub={`At full deployment · 1:${CASELOAD_CAP} caseload cap`}
                />
                <Stat
                  testId="sizing-output-spokes"
                  icon={Briefcase}
                  label="Spoke Portfolio Size"
                  value={fmtNumber(projection.perSpoke)}
                  sub="Households per Spoke (3 Spokes)"
                />
                <Stat
                  testId="sizing-output-5yr"
                  icon={TrendingUp}
                  label="5-Year All-In"
                  value={fmtCurrency(projection.fiveYear)}
                  sub="Year 1 × 5.45 (4% annual + closeout)"
                />
              </div>

              {/* Caveat + CTA */}
              <div className="border-t border-[#FDF8EF]/15 pt-5 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
                <p className="text-xs text-[#CBD5E1] flex items-start gap-2 max-w-md">
                  <Info className="h-4 w-4 text-[#B8860B] shrink-0 mt-0.5" />
                  Projection is a starting frame, not a quote. Final scale is set
                  jointly with George, your CDBG planner, and the three selected
                  Spoke agencies during the 90-day kickoff.
                </p>
                <a
                  href="#contact"
                  data-testid="sizing-apply-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#B8860B] hover:bg-[#D4AF37] text-[#1F2937] h-11 px-5 font-mono text-[10px] uppercase tracking-widest whitespace-nowrap transition-colors"
                >
                  Apply with these numbers <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-[#FDF8EF]/5 border border-[#FDF8EF]/15 p-5 backdrop-blur"
    >
      <div className="flex items-center justify-between mb-3">
        <Icon className="h-4 w-4 text-[#B8860B]" />
        <span className="font-mono text-[9px] uppercase tracking-widest text-[#B8860B]">
          {label}
        </span>
      </div>
      <div className="font-display text-3xl text-[#FDF8EF] leading-none">{value}</div>
      <div className="font-mono text-[10px] text-[#94A3B8] mt-2 leading-relaxed">{sub}</div>
    </div>
  );
}
